import React, { useState, useEffect } from 'react';
import { LuArrowLeft, LuChevronDown } from 'react-icons/lu';
import { useHPCStore } from '../../../store/hpcStore';
import Card from '../../../components/common/Card';
import Badge from '../../../components/common/Badge';
import Notice from '../../../components/common/Notice';
import EmptyState from '../../../components/common/EmptyState';
import LoadingMessage from '../../../components/common/LoadingMessage';
import ErrorWidget from '../../../components/common/ErrorWidget';
import { ResultType } from '../../../models/hpc/hpc_learning_outcome';
import Logger from '../../../utils/logger';

const HPCReportDetailsView = ({ reportId, onBack }) => {
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedSections, setExpandedSections] = useState(new Set());

  const {
    hpcReports,
    studentHPCReports,
    loading: storeLoading,
    error: storeError
  } = useHPCStore();

  useEffect(() => {
    const findReportData = () => {
      if (!reportId) {
        setError('Report ID is required');
        return;
      }

      try {

        
        // First try to find in hpcReports (for HPC reports tab)
        let foundReport = hpcReports.find(report => report.id === reportId);
        
        // If not found, try studentHPCReports (for student reports tab)
        if (!foundReport) {
          foundReport = studentHPCReports.find(report => report.id === reportId);
        }

        if (foundReport) {
          Logger.log('Successfully found report:', foundReport);
          setReportData(foundReport);
          setError(null);
        } else {
          Logger.log('Report not found with ID:', reportId);
          setError('Report not found in current data');
        }
      } catch (err) {
        Logger.error('Error finding report:', err);
        setError(err.message || 'An error occurred while loading the report');
      }
    };

    findReportData();
  }, [reportId, hpcReports, studentHPCReports]);

  const getResultTypeLabel = (resultType) => {
    switch (resultType) {
      case ResultType.CHOICE:
        return 'Multiple choice';
      case ResultType.TEXT:
        return 'Text answer';
      case ResultType.SCORE:
        return 'Score';
      case ResultType.IMAGE:
        return 'Image';
      case ResultType.STUDENT_DETAILS:
        return 'Student details';
      default:
        return 'Other';
    }
  };

  const renderLearningOutcome = (outcome, index) => {
    return (
      <li key={outcome.id || index} className="rounded-lg border border-ui-line p-4">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <h4 className="text-sm font-medium text-ui-ink">{outcome.label}</h4>
          <Badge tone="grey">{getResultTypeLabel(outcome.resultType)}</Badge>
        </div>

        {outcome.resultType === ResultType.CHOICE && outcome.choices && (
          <div className="mt-3">
            <p className="mb-1.5 text-xs font-medium text-ui-ink-3">Options</p>
            <div className="flex flex-wrap gap-1.5">
              {outcome.choices.map((choice, choiceIndex) => {
                const isChosen = outcome.result === choice;
                return (
                  <span
                    key={choiceIndex}
                    className={`rounded-md px-2.5 py-1 text-xs ring-1 ring-inset ${
                      isChosen
                        ? 'bg-emerald-50 font-medium text-emerald-800 ring-emerald-600/30 dark:bg-emerald-500/10 dark:text-emerald-300'
                        : 'text-ui-ink-2 ring-ui-line'
                    }`}
                  >
                    {choice}
                  </span>
                );
              })}
            </div>
          </div>
        )}

        {outcome.result && (
          <div className="mt-3 border-t border-ui-line-soft pt-3">
            <p className="mb-1 text-xs font-medium text-ui-ink-3">Result</p>
            {outcome.resultType === ResultType.IMAGE ? (
              <img
                src={outcome.result}
                alt={outcome.label}
                className="h-auto max-h-48 max-w-full rounded-md border border-ui-line object-contain"
              />
            ) : (
              <p className="text-sm text-ui-ink">{outcome.result}</p>
            )}
          </div>
        )}
      </li>
    );
  };

  const renderSectionCard = (section, index) => {
    const isExpanded = isSectionExpanded(section.id, index);
    const learningOutcomesCount = section.learningOutcomes ? section.learningOutcomes.length : 0;

    return (
      <div key={section.id || index} className="overflow-hidden rounded-xl border border-ui-line bg-ui-card shadow-card">
        <button
          type="button"
          onClick={() => toggleSectionExpansion(section.id, index)}
          aria-expanded={isExpanded}
          className="flex w-full items-center gap-3 px-5 py-4 text-left transition-colors hover:bg-ui-muted"
        >
          <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-ui-brand-soft text-xs font-semibold text-ui-brand-soft-ink">
            {index + 1}
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-[15px] font-semibold text-ui-ink">{section.title || `Section ${index + 1}`}</span>
            <span className="mt-0.5 block text-xs text-ui-ink-3">{learningOutcomesCount} learning outcomes</span>
          </span>
          <LuChevronDown aria-hidden className={`size-5 shrink-0 text-ui-ink-3 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
        </button>

        {(isExpanded || section.objective) && (
          <div className="flex flex-col gap-4 px-5 pb-5 pl-[60px]">
            {section.objective && (
              <p className="rounded-lg bg-ui-muted px-3 py-2 text-sm text-ui-ink-2">
                <span className="font-medium text-ui-ink">Objective: </span>
                {section.objective}
              </p>
            )}

            {isExpanded && section.aiSummary && (
              <Notice tone="info" title="AI summary">{section.aiSummary}</Notice>
            )}

            {isExpanded && section.learningOutcomes && section.learningOutcomes.length > 0 && (
              <div>
                <h4 className="mb-2 text-[11px] font-semibold tracking-wider text-ui-ink-3 uppercase">
                  Learning outcomes ({section.learningOutcomes.length})
                </h4>
                <ul className="flex flex-col gap-2">{section.learningOutcomes.map(renderLearningOutcome)}</ul>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const toggleSectionExpansion = (sectionId, index) => {
    const key = sectionId || `section-${index}`;
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(key)) {
      newExpanded.delete(key);
    } else {
      newExpanded.add(key);
    }
    setExpandedSections(newExpanded);
  };

  const isSectionExpanded = (sectionId, index) => {
    const key = sectionId || `section-${index}`;
    return expandedSections.has(key);
  };

  const back = (
    <button
      type="button"
      onClick={onBack}
      className="mb-1.5 inline-flex items-center gap-1.5 rounded text-[13px] font-medium text-ui-ink-2 transition-colors hover:text-ui-ink"
    >
      <LuArrowLeft aria-hidden className="size-4" />
      HPC reports
    </button>
  );

  // Show loading if store is loading or we haven't found the report yet and no error
  if (loading || storeLoading || (!reportData && !error)) {
    return <LoadingMessage message="Loading the HPC report…" />;
  }

  if (error || storeError) {
    return (
      <div className="mx-auto max-w-6xl">
        {back}
        <ErrorWidget error={{ message: error || storeError }} title="Could not open the HPC report" onRetry={() => window.location.reload()} />
      </div>
    );
  }

  if (!reportData) {
    return (
      <div className="mx-auto max-w-6xl">
        {back}
        <Card>
          <EmptyState title="Report not found" message="This HPC report could not be found." />
        </Card>
      </div>
    );
  }

  const totalOutcomes = reportData.sections
    ? reportData.sections.reduce((total, section) => total + (section.learningOutcomes ? section.learningOutcomes.length : 0), 0)
    : 0;

  const overview = [
    { label: 'Sections', value: reportData.sections ? reportData.sections.length : 0 },
    { label: 'Learning outcomes', value: totalOutcomes },
    { label: 'Classes', value: reportData.classes ? reportData.classes.length : 0 },
  ];

  return (
    <div className="mx-auto max-w-6xl">
      {back}
      <div className="mb-5">
        <h1 className="text-2xl font-semibold tracking-tight text-ui-ink">{reportData.name}</h1>
        <p className="mt-1 text-sm text-ui-ink-2">Classes: {reportData.classes ? reportData.classes.join(', ') : '—'}</p>
      </div>

      <dl className="mb-6 grid grid-cols-3 gap-4">
        {overview.map((item) => (
          <div key={item.label} className="rounded-xl border border-ui-line bg-ui-card px-5 py-4 shadow-card">
            <dt className="text-sm font-medium text-ui-ink-2">{item.label}</dt>
            <dd className="mt-0.5 text-[28px] leading-9 font-semibold text-ui-ink tabular-nums">{item.value}</dd>
          </div>
        ))}
      </dl>

      <h2 className="mb-3 text-[15px] font-semibold text-ui-ink">Sections</h2>
      {reportData.sections && reportData.sections.length > 0 ? (
        <div className="flex flex-col gap-3">{reportData.sections.map((section, index) => renderSectionCard(section, index))}</div>
      ) : (
        <Card>
          <EmptyState title="No sections yet" message="This report has no sections yet." />
        </Card>
      )}
    </div>
  );
};

export default HPCReportDetailsView;
